import { initProgressBar } from './progress-bar';
import createFormStepStore from '../stores/form-step-store';
import createFormStore from '../stores/form-values-store';

import { SlideEntry, FunnelRule, RuleSet } from '../types';
import initStep from './step';
import { computed } from 'nanostores';
import initCalendarStore from '../stores/calendar-store';
import initBookedAppointmentStore from '../stores/booked-appointment-store';
import { createFormStepEvent, createRejectionEvent } from '../funnel-tracking';
import { pageSettings } from '../../general/utils/ga4-tracking/page-settings';
import { pushToDataLayer } from '../../general/utils/ga4-tracking/push-to-data-layer';
import { debounce } from '../../general/utils/utils';

export default async function initFunnel(funnel: HTMLElement) {
  // Dom Elements
  const container = funnel.querySelector('.container') as HTMLElement;
  const funnelWrapper = funnel.querySelector('.funnel-wrapper') as HTMLElement;
  const stepWrapper = funnel.querySelector('.step-wrapper') as HTMLElement;
  const currentStepElement = funnelWrapper.querySelector('.funnel-step') as HTMLElement;
  const hasAppointments = funnel.hasAttribute('data-has-appointment');
  const slides = getSlideEntries(funnel.querySelectorAll('template'));
  const funnelRuleString = funnel.dataset.rules;
  const funnelRules: FunnelRule[] = [];

  if (funnelRuleString) {
    funnelRuleString.split('#').forEach(singleRuleString => {
      const [ruleSets, slideId] = singleRuleString.split('=');
      const rules: RuleSet[] = [];
      ruleSets.split('|').forEach(singleRule => {
        const [field, operator, value] = singleRule.split(',');
        rules.push({ field: field, operator: operator, value: value });
      });
      funnelRules.push({ slideId: slideId, rules: rules });
    });
  }
  const leadSource = funnel.dataset.lead_source;

  // Store initialization
  const formStepStore = createFormStepStore(slides);
  const formStore = createFormStore({}, { isExpat: pageSettings.isExpat, withCalendar: hasAppointments, leadSource });
  const calendarStore = hasAppointments ? initCalendarStore(computed(formStore.values, formValues => formValues.calendar)) : undefined;
  const bookedAppointmentStore = hasAppointments ? initBookedAppointmentStore() : undefined;

  // progressbar animation
  const progressbar = funnel.querySelector<HTMLElement>('.progressbar, .hero-progressbar');
  if (progressbar) {
    initProgressBar(progressbar, formStepStore);
  }

  //initialisation of first step
  await initStep(formStepStore.currentStep.get().title, currentStepElement, { formStore, progressStore: formStepStore, calendarStore, bookedAppointmentStore }, funnelRules);

  fixElementToHeight(container, funnelWrapper);
  addEventListener(
    'resize',
    debounce(() => {
      fixElementToHeight(container, funnelWrapper);
    }, 150)
  );

  const interactionEvents = ['click', 'focus'];
  const onInteraction = () => {
    interactionEvents.forEach(event => funnelWrapper.removeEventListener(event, onInteraction, { capture: true }));
    pushToDataLayer(createFormStepEvent(formStore.parsed.get(), formStepStore.history.get(), true));
  };
  interactionEvents.forEach(event => funnelWrapper.addEventListener(event, onInteraction, { capture: true }));

  // initialisation of steps and tracking on navigation
  formStepStore.history.listen(async $history => {
    const currentStep = $history[$history.length - 1];
    const newActiveSlideNode = currentStep.template.content.cloneNode(true) as DocumentFragment;
    pushToDataLayer(createFormStepEvent(formStore.parsed.get(), $history));
    // trigger rejection event in case of rejection tracking field
    if (currentStep.tracking.indexOf('rej_') !== -1) {
      formStore.submit({}, true); // get insurance values from pagedefaults into formstore
      pushToDataLayer(createRejectionEvent(formStore.parsed.get(), $history));
    }
    stepWrapper.classList.add('in-transition');
    setTimeout(async () => {
      stepWrapper.replaceChildren(newActiveSlideNode);
      await initStep(currentStep.title, stepWrapper, { formStore, progressStore: formStepStore, calendarStore, bookedAppointmentStore }, funnelRules);
      fixElementToHeight(container, funnelWrapper);
      stepWrapper.classList.remove('in-transition');
      (stepWrapper.firstElementChild as HTMLElement)?.focus();
    }, 600);
  });
}
/* ---------- helpers ---------- */

function getSlideEntries(templates: NodeListOf<HTMLTemplateElement>): SlideEntry[] {
  return Array.from(templates)
    .map(template => {
      return {
        id: template.id,
        order: parseInt(template.dataset.order as string),
        title: template.dataset.title || template.content.querySelector<HTMLElement>('h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6')?.innerText || '',
        fields: new Set(Array.from(template.content.querySelectorAll<HTMLInputElement>('input[name]').values()).map(input => input.name)),
        tracking: template.dataset.tracking || 'na',
        template
      };
    })
    .sort((a, b) => a.order - b.order);
}

function fixElementToHeight(container: HTMLElement, funnelWrapper: HTMLElement) {
  // Wrapper height
  const funnelWrapperComputedStyle = window.getComputedStyle(funnelWrapper);
  const funnelWrapperPaddingTop = parseFloat(funnelWrapperComputedStyle.paddingTop);
  const funnelWrapperPaddingBottom = parseFloat(funnelWrapperComputedStyle.paddingBottom);
  const progressBarHeight = getProgressBarHeight(funnelWrapper);
  const wrapperHeight = (funnelWrapper.querySelector<HTMLElement>('.funnel-step')?.offsetHeight || 0) + funnelWrapperPaddingTop + funnelWrapperPaddingBottom + progressBarHeight;
  funnelWrapper.style.height = `${wrapperHeight}px`;

  // container height
  const headerHeight = getFunnelHeadHeight(container);
  const containerComputedStyle = window.getComputedStyle(container);
  const containerPaddingTop = parseFloat(containerComputedStyle.paddingTop);
  const containerPaddingBottom = parseFloat(containerComputedStyle.paddingBottom);
  container.style.height = `${wrapperHeight + headerHeight + containerPaddingTop + containerPaddingBottom}px`;
}

function getProgressBarHeight(funnelWrapper: HTMLElement) {
  const progressBar = funnelWrapper.querySelector<HTMLElement>('.progressbar, .hero-progressbar');
  if (!progressBar || progressBar.classList.contains('is-hidden') || progressBar.classList.contains('fade-out')) {
    const resultHeadline = funnelWrapper.querySelector<HTMLElement>('.h3.result-headline');
    if (resultHeadline) {
      const computedStyles = window.getComputedStyle(resultHeadline);
      const marginTop = parseFloat(computedStyles.marginTop);
      const marginBottom = parseFloat(computedStyles.marginBottom);
      return resultHeadline.offsetHeight + marginTop + marginBottom;
    }
    return 0;
  }
  const computedStyles = window.getComputedStyle(progressBar);
  const marginTop = parseFloat(computedStyles.marginTop);
  const marginBottom = parseFloat(computedStyles.marginBottom);
  return progressBar.offsetHeight + marginTop + marginBottom;
}

function getFunnelHeadHeight(container: HTMLElement) {
  const funnelHead = container.querySelector<HTMLElement>('.funnel-head');
  if (!funnelHead) {
    return 0;
  }
  const computedStyles = window.getComputedStyle(funnelHead);
  const marginTop = parseFloat(computedStyles.marginTop);
  const marginBottom = parseFloat(computedStyles.marginBottom);
  return funnelHead.offsetHeight + marginTop + marginBottom;
}
